if (!customElements.get('mini-bundle-builder')) {
  class MiniBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map();
      this.targetCount = parseInt(this.dataset.defaultCount, 10) || 4;
      this.tierDiscount = 0;
      this.isSubmitting = false;
      this.filters = { search: '', sort: 'best', themes: new Set() };
      this.isLoadingAll = false;
    }

    connectedCallback() {
      this.section = this.closest('section.mini-bundle');
      this.tierButtons = Array.from(this.section.querySelectorAll('.mini-bundle__tier'));
      this.submitButton = this.querySelector('[data-submit]');
      this.submitText = this.querySelector('[data-submit-text]');
      this.submitSpinner = this.querySelector('[data-submit-spinner]');
      this.errorEl = this.querySelector('[data-error]');
      this.summaryList = this.querySelector('[data-summary-list]');
      this.summaryPlaceholder = this.querySelector('[data-summary-placeholder]');
      this.selectedCountEl = this.querySelector('[data-selected-count]');
      this.targetCountEl = this.querySelector('[data-target-count]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.subtotalEl = this.querySelector('[data-subtotal]');
      this.savingsRow = this.querySelector('[data-savings-row]');
      this.discountPercentEl = this.querySelector('[data-discount-percent]');
      this.finalTotalEl = this.querySelector('[data-final-total]');
      this.detailsToggle = this.querySelector('[data-toggle-details]');
      this.detailsPanel = this.querySelector('[data-details]');
      this.barEl = this.querySelector('[data-summary]');
      this.successEl = this.querySelector('[data-bar-success]');
      this.successDetailEl = this.querySelector('[data-bar-success-detail]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.successResetTimer = null;

      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      if (this.tierButtons.length > 0) {
        const firstActive = this.tierButtons.find((btn) => btn.getAttribute('aria-checked') === 'true') || this.tierButtons[0];
        this.setActiveTier(firstActive);
        this.tierButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.setActiveTier(btn));
        });
      }

      this.grid = this.querySelector('[data-mini-grid]');
      this.isLoadingMore = false;

      this.searchInput = this.querySelector('[data-mini-search]');
      this.pillBar = this.querySelector('[data-mini-pills]');
      this.noResultsEl = this.querySelector('[data-mini-no-results]');

      this.addEventListener('click', this.handleClick.bind(this));
      this.submitButton.addEventListener('click', this.handleSubmit.bind(this));

      if (this.detailsToggle && this.detailsPanel) {
        this.detailsToggle.addEventListener('click', this.toggleDetails.bind(this));
      }

      if (this.searchInput) {
        const onInput = () => {
          this.filters.search = (this.searchInput.value || '').trim().toLowerCase();
          this.handleFilterChange();
        };
        this.searchInput.addEventListener('input', typeof debounce === 'function' ? debounce(onInput, 200) : onInput);
      }

      if (this.pillBar) {
        this.pillBar.addEventListener('click', this.handlePillClick.bind(this));
      }

      this.renderSummary();
    }

    handlePillClick(event) {
      const pill = event.target.closest('.mini-bundle__pill');
      if (!pill) return;
      if (pill.dataset.sort) {
        this.setSort(pill.dataset.sort);
      } else if (pill.hasAttribute('data-theme-all')) {
        this.clearThemes();
      } else if (pill.dataset.theme) {
        this.toggleTheme(pill.dataset.theme, pill);
      }
    }

    clearThemes() {
      if (this.filters.themes.size === 0) return;
      this.filters.themes.clear();
      this.pillBar.querySelectorAll('[data-theme]').forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('aria-pressed', 'false');
      });
      this.updateAllPill();
      this.handleFilterChange();
    }

    updateAllPill() {
      const allPill = this.pillBar && this.pillBar.querySelector('[data-theme-all]');
      if (!allPill) return;
      const active = this.filters.themes.size === 0;
      allPill.classList.toggle('is-active', active);
      allPill.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    setSort(sort) {
      if (this.filters.sort === sort) return;
      this.filters.sort = sort;
      this.pillBar.querySelectorAll('[data-sort]').forEach((p) => {
        const active = p.dataset.sort === sort;
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.handleFilterChange();
    }

    toggleTheme(theme, pill) {
      if (this.filters.themes.has(theme)) {
        this.filters.themes.delete(theme);
        pill.classList.remove('is-active');
        pill.setAttribute('aria-pressed', 'false');
      } else {
        this.filters.themes.add(theme);
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');
      }
      this.updateAllPill();
      this.handleFilterChange();
    }

    hasActiveFilter() {
      return this.filters.search !== '' || this.filters.themes.size > 0 || this.filters.sort !== 'best';
    }

    async handleFilterChange() {
      this.applyFiltersAndSort();
      if (this.hasActiveFilter()) {
        await this.ensureAllLoaded();
      }
    }

    async ensureAllLoaded() {
      if (this.isLoadingAll) return;
      let btn = this.querySelector('[data-load-more]');
      if (!btn) return;
      this.isLoadingAll = true;
      this.classList.add('is-loading-all');
      try {
        let lastUrl = null;
        while (btn && !btn.disabled) {
          if (btn.dataset.nextUrl === lastUrl) break;
          lastUrl = btn.dataset.nextUrl;
          await this.handleLoadMore(btn);
          btn = this.querySelector('[data-load-more]');
        }
      } finally {
        this.isLoadingAll = false;
        this.classList.remove('is-loading-all');
        this.applyFiltersAndSort();
      }
    }

    applyFiltersAndSort() {
      if (!this.grid) return;
      const q = this.filters.search;
      const themes = this.filters.themes;
      const cards = Array.from(this.grid.querySelectorAll('.mini-bundle__card'));
      let visibleCount = 0;

      cards.forEach((card) => {
        const title = card.dataset.title || '';
        const tags = card.dataset.tags || '';
        let visible = true;
        if (q && !title.includes(q) && !tags.includes(q)) visible = false;
        if (visible && themes.size > 0) {
          let matched = false;
          for (const t of themes) {
            if (tags.includes(t)) { matched = true; break; }
          }
          if (!matched) visible = false;
        }
        card.hidden = !visible;
        if (visible) visibleCount++;
      });

      cards.sort((a, b) => {
        if (this.filters.sort === 'latest') {
          return (parseInt(b.dataset.createdAt, 10) || 0) - (parseInt(a.dataset.createdAt, 10) || 0);
        }
        return (parseInt(a.dataset.popularityIndex, 10) || 0) - (parseInt(b.dataset.popularityIndex, 10) || 0);
      });

      const frag = document.createDocumentFragment();
      cards.forEach((c) => frag.appendChild(c));
      this.grid.appendChild(frag);

      if (this.noResultsEl) {
        this.noResultsEl.hidden = visibleCount > 0;
      }
    }

    toggleDetails() {
      const isOpen = this.detailsToggle.getAttribute('aria-expanded') === 'true';
      this.detailsToggle.setAttribute('aria-expanded', !isOpen);
      this.detailsPanel.hidden = isOpen;
    }

    enterSuccessState(itemCount, savingsText) {
      if (!this.barEl || !this.successEl) return;
      this.barEl.classList.add('is-success');
      this.successEl.hidden = false;
      if (this.successDetailEl) {
        const summary = savingsText
          ? `${itemCount} minis added — ${savingsText}`
          : `${itemCount} minis added to your cart.`;
        this.successDetailEl.textContent = summary;
      }
      if (this.detailsPanel && !this.detailsPanel.hidden) {
        this.detailsPanel.hidden = true;
        if (this.detailsToggle) this.detailsToggle.setAttribute('aria-expanded', 'false');
      }
      clearTimeout(this.successResetTimer);
      this.successResetTimer = setTimeout(() => this.exitSuccessState(), 6000);
    }

    exitSuccessState() {
      clearTimeout(this.successResetTimer);
      this.successResetTimer = null;
      if (this.barEl) this.barEl.classList.remove('is-success');
      if (this.successEl) this.successEl.hidden = true;
    }

    setActiveTier(btn) {
      this.exitSuccessState();
      this.tierButtons.forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
      this.targetCount = parseInt(btn.dataset.tierCount, 10) || 4;
      this.tierDiscount = parseFloat(btn.dataset.tierDiscount) || 0;

      // Clamp existing selections if they exceed new target.
      let total = this.totalSelected();
      if (total > this.targetCount) {
        for (const [variantId, item] of Array.from(this.selections.entries()).reverse()) {
          while (item.qty > 0 && total > this.targetCount) {
            item.qty -= 1;
            total -= 1;
          }
          if (item.qty === 0) this.selections.delete(variantId);
          if (total <= this.targetCount) break;
        }
      }

      this.renderSummary();
    }

    handleClick(event) {
      const actionBtn = event.target.closest('[data-action]');
      if (actionBtn) {
        const variantId = actionBtn.dataset.variantId;
        const action = actionBtn.dataset.action;
        if (action === 'increment') {
          this.increment(variantId);
        } else if (action === 'decrement') {
          this.decrement(variantId);
        }
        return;
      }
      const loadMoreBtn = event.target.closest('[data-load-more]');
      if (loadMoreBtn && !loadMoreBtn.disabled) {
        this.handleLoadMore(loadMoreBtn);
      }
    }

    async handleLoadMore(btn) {
      if (this.isLoadingMore) return;
      const nextUrl = btn.dataset.nextUrl;
      const sectionId = this.dataset.sectionId;
      if (!nextUrl || !sectionId || !this.grid) return;

      this.isLoadingMore = true;
      const originalLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Loading…';

      try {
        const separator = nextUrl.includes('?') ? '&' : '?';
        const url = `${nextUrl}${separator}section_id=${encodeURIComponent(sectionId)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-mini-grid]');
        if (newGrid) {
          const fragment = document.createDocumentFragment();
          newGrid.querySelectorAll('.mini-bundle__card').forEach((card) => {
            fragment.appendChild(card);
          });
          this.grid.appendChild(fragment);
        }

        const newLoadMore = doc.querySelector('[data-load-more]');
        if (newLoadMore) {
          btn.dataset.nextUrl = newLoadMore.dataset.nextUrl;
          btn.textContent = originalLabel;
          btn.disabled = false;
        } else {
          const wrap = btn.closest('.mini-bundle__load-more-wrap');
          if (wrap) wrap.remove();
        }

        this.applyFiltersAndSort();
        this.renderSummary();
      } catch (err) {
        console.error('Load more minis failed:', err);
        btn.textContent = originalLabel;
        btn.disabled = false;
      } finally {
        this.isLoadingMore = false;
      }
    }

    increment(variantId) {
      if (this.totalSelected() >= this.targetCount) return;
      this.exitSuccessState();
      const display = this.querySelector(`[data-qty-display][data-variant-id="${variantId}"]`);
      if (!display) return;
      const current = this.selections.get(variantId);
      if (current) {
        current.qty += 1;
      } else {
        this.selections.set(variantId, {
          variantId,
          qty: 1,
          price: parseInt(display.dataset.price, 10) || 0,
          title: display.dataset.title,
          image: display.dataset.image,
          handle: display.dataset.handle,
        });
      }
      this.renderSummary();
    }

    decrement(variantId) {
      const current = this.selections.get(variantId);
      if (!current) return;
      current.qty -= 1;
      if (current.qty <= 0) this.selections.delete(variantId);
      this.renderSummary();
    }

    totalSelected() {
      let total = 0;
      for (const item of this.selections.values()) total += item.qty;
      return total;
    }

    subtotalCents() {
      let total = 0;
      for (const item of this.selections.values()) total += item.qty * item.price;
      return total;
    }

    formatMoney(cents) {
      // Minimal money formatter that respects the shop's money_format placeholder.
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      const withCommas = (n) => {
        const parts = n.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
      };
      let out = this.moneyFormat;
      out = out.replace(/\{\{\s*amount\s*\}\}/g, withCommas(amount));
      out = out.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(amountNoDecimals));
      out = out.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
      out = out.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals);
      out = out.replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, withCommas(amount).replace(/,/g, ' '));
      out = out.replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withCommas(amount).replace(/,/g, "'"));
      return out;
    }

    renderSummary() {
      const total = this.totalSelected();
      const target = this.targetCount;
      const subtotal = this.subtotalCents();
      const savings = Math.round((subtotal * this.tierDiscount) / 100);
      const finalTotal = subtotal - savings;
      const complete = total === target && target > 0;

      this.selectedCountEl.textContent = total;
      this.targetCountEl.textContent = target;
      this.progressFill.style.width = `${Math.min(100, (total / target) * 100)}%`;

      this.subtotalEl.textContent = this.formatMoney(subtotal);
      if (savings > 0) {
        this.savingsRow.hidden = false;
        this.discountPercentEl.textContent = this.tierDiscount;
      } else {
        this.savingsRow.hidden = true;
      }
      this.finalTotalEl.textContent = this.formatMoney(finalTotal);

      // Per-card qty displays + button states
      this.querySelectorAll('[data-qty-display]').forEach((display) => {
        const variantId = display.dataset.variantId;
        const sel = this.selections.get(variantId);
        const qty = sel ? sel.qty : 0;
        display.textContent = qty;

        const card = display.closest('.mini-bundle__card');
        const minus = card.querySelector('[data-action="decrement"]');
        const plus = card.querySelector('[data-action="increment"]');

        minus.disabled = qty === 0;
        plus.disabled = total >= target;
        card.classList.toggle('is-selected', qty > 0);
      });

      // Summary list
      this.summaryPlaceholder.hidden = this.selections.size > 0;
      Array.from(this.summaryList.querySelectorAll('[data-summary-item]')).forEach((el) => el.remove());
      for (const item of this.selections.values()) {
        const li = document.createElement('li');
        li.className = 'mini-bundle__summary-item';
        li.setAttribute('data-summary-item', '');
        li.innerHTML = `
          <img class="mini-bundle__summary-img" src="${item.image}" alt="" loading="lazy" width="48" height="48">
          <div class="mini-bundle__summary-text">
            <span class="mini-bundle__summary-name">${item.title}</span>
            <span class="mini-bundle__summary-qty">× ${item.qty}</span>
          </div>
          <span class="mini-bundle__summary-line-price">${this.formatMoney(item.price * item.qty)}</span>
        `;
        this.summaryList.appendChild(li);
      }

      // Submit state
      if (complete) {
        this.submitButton.disabled = false;
        this.submitText.textContent = `Add bundle to cart`;
      } else {
        this.submitButton.disabled = true;
        const remaining = Math.max(0, target - total);
        this.submitText.textContent = remaining === target ? `Pick ${target} minis` : `Pick ${remaining} more`;
      }
    }

    async handleSubmit() {
      if (this.isSubmitting) return;
      if (this.totalSelected() !== this.targetCount) return;

      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.submitSpinner.hidden = false;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';

      const bundleTag = `mini-${this.targetCount}`;
      const items = [];
      for (const sel of this.selections.values()) {
        items.push({
          id: parseInt(sel.variantId, 10),
          quantity: sel.qty,
          properties: {
            _bundle: bundleTag,
            _no_canvas_type: 'true',
          },
        });
      }

      // Bulk adds don't play nice with cart-notification (it expects a single item key),
      // so only request the icon bubble — we update that manually.
      const body = {
        items,
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
        }

        // Capture details for the success banner BEFORE clearing selections.
        const addedCount = this.totalSelected();
        const savings = Math.round((this.subtotalCents() * this.tierDiscount) / 100);
        const savingsText = savings > 0
          ? `you saved ${this.formatMoney(savings)} (${this.tierDiscount}% off)`
          : '';

        // Reset selections and show the in-bar success state immediately —
        // these are guaranteed because the POST succeeded.
        this.selections.clear();
        this.renderSummary();
        this.enterSuccessState(addedCount, savingsText);

        // Best-effort UI refreshes (don't let failures here block the success state).
        try {
          if (data.sections && data.sections['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              bubble.innerHTML = new DOMParser()
                .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
                .querySelector('.shopify-section').innerHTML;
            }
          }
        } catch (_) { /* ignore */ }

        try {
          if (window.dataLayer && Array.isArray(data.items)) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
                value: (data.items.reduce((sum, i) => sum + (parseFloat(i.final_line_price) || 0), 0) / 100).toFixed(2),
                items: data.items.map((i) => ({
                  item_id: i.product_id && i.product_id.toString(),
                  item_name: i.product_title,
                  variant_id: i.variant_id && i.variant_id.toString(),
                  variant_name: i.variant_title,
                  price: (parseFloat(i.final_price) / 100).toFixed(2),
                  quantity: i.quantity,
                })),
              },
            });
          }
        } catch (_) { /* ignore */ }

        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'mini-bundle-builder',
              cartData: data,
            });
          }
        } catch (_) { /* ignore */ }
      } catch (err) {
        console.error('Mini bundle add failed:', err);
        this.errorEl.textContent = err.message || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
        // Open the details panel so the error message is visible.
        if (this.detailsPanel) this.detailsPanel.hidden = false;
        if (this.detailsToggle) this.detailsToggle.setAttribute('aria-expanded', 'true');
      } finally {
        this.isSubmitting = false;
        this.submitSpinner.hidden = true;
        // renderSummary already re-evaluates disabled state, but we may have errored mid-flight.
        if (this.totalSelected() !== this.targetCount) {
          this.submitButton.disabled = true;
        } else {
          this.submitButton.disabled = false;
        }
      }
    }
  }

  customElements.define('mini-bundle-builder', MiniBundleBuilder);
}
